import { Component } from 'react';

import { getDatadog } from '../../services/datadog';

import type { ReactNode, ErrorInfo } from 'react';
import type { Logger } from '@datadog/browser-logs';

interface ErrorBoundaryProps {
  fallback?: ReactNode;
  children?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  logger: Promise<Logger>;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.logger = getDatadog().then((dd) => dd.createLogger('error-boundary'));
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo?: ErrorInfo) {
    this.logger
      .then((logger) => {
        logger.error(
          `component did catch: ${error.message}`,
          { react: { componentStack: errorInfo?.componentStack } },
          error
        );
      })
      .catch(() => {});

    console.error(error);
  }

  render() {
    const { fallback, children } = this.props;

    if (this.state.hasError && typeof fallback !== 'undefined') {
      return fallback;
    }

    return children;
  }
}
